import { useCallback, useEffect, useState } from "react";
import { Button, Card, Select, Space, Table, Tag, Tooltip, message } from "antd";
import { HeartOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { api, type HealthCheckRow, type NotifChannel } from "../api";
import { useRole } from "../contexts/AuthContext";

function when(v?: string) {
  return v ? new Date(v).toLocaleString() : "—";
}

// System Health: sflow's view of its own health (mirror, CDN list, sensor
// process, each sensor, updater), where its alerts go, and a way to re-check
// right after fixing something.
export default function SystemHealthPanel() {
  const { isAdmin } = useRole();
  const [checks, setChecks] = useState<HealthCheckRow[]>([]);
  const [channels, setChannels] = useState<NotifChannel[]>([]);
  const [channelId, setChannelId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, s, chs] = await Promise.all([
        api.healthSystem(),
        api.healthSettings(),
        api.notifChannels().catch(() => [] as NotifChannel[]),
      ]);
      setChecks(h.checks);
      setChannelId(s.channel_id);
      setChannels(chs.filter((c) => c.type === "telegram"));
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load system health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function saveChannel(id: number | null) {
    try {
      const r = await api.setHealthSettings(id);
      setChannelId(r.channel_id);
      message.success(r.channel_id ? "System alerts will go to this channel" : "System alerts: UI only");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Save failed");
    }
  }

  async function checkNow() {
    setChecking(true);
    try {
      const h = await api.healthCheckNow();
      if (h.checks.length === 0) {
        message.warning("No results: the health state could not be loaded (see the sfconsole log)");
        return;
      }
      setChecks(h.checks);
      if (h.failing) {
        message.warning(`${h.failing} check(s) failing`);
      } else {
        message.success("All checks OK");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Check failed");
    } finally {
      setChecking(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    try {
      await api.healthTest();
      message.success("Test message sent");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Send failed");
    } finally {
      setTesting(false);
    }
  }

  const adminOnly = isAdmin ? undefined : "Admin only";

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <HeartOutlined />
          <span style={{ fontWeight: 700 }}>System Health</span>
        </Space>
      }
      extra={
        // The span lets the tooltip show while the button is disabled.
        <Tooltip title={adminOnly}>
          <span style={{ display: "inline-block" }}>
            <Button size="small" icon={<ReloadOutlined />} loading={checking} disabled={!isAdmin} onClick={checkNow}>
              Check now
            </Button>
          </span>
        </Tooltip>
      }
    >
      <Table<HealthCheckRow>
        size="small"
        rowKey="key"
        loading={loading}
        pagination={false}
        dataSource={checks}
        locale={{ emptyText: "No checks have run yet — the first run is 5 minutes after start" }}
        columns={[
          { title: "Check", dataIndex: "name" },
          {
            title: "Status",
            dataIndex: "status",
            width: 100,
            render: (v: string) => (v === "failing" ? <Tag color="red">Failing</Tag> : <Tag color="green">OK</Tag>),
          },
          { title: "Since", dataIndex: "since", width: 180, render: (v: string) => when(v) },
          { title: "Last checked", dataIndex: "last_checked", width: 180, render: (v?: string) => when(v) },
          {
            title: "Detail",
            dataIndex: "detail",
            render: (v?: string) => v || <span style={{ color: "var(--muted)" }}>—</span>,
          },
        ]}
      />
      <Space style={{ marginTop: 12 }} wrap>
        <span>Alert channel:</span>
        <Tooltip title={adminOnly}>
          <Select<number | "">
            style={{ minWidth: 260 }}
            disabled={!isAdmin}
            value={channelId ?? ""}
            onChange={(v) => saveChannel(v === "" ? null : v)}
            options={[
              { value: "", label: "None — show in UI only" },
              ...channels.map((c) => ({ value: c.id, label: c.enabled ? c.name : `${c.name} (disabled)` })),
            ]}
          />
        </Tooltip>
        {/* The span lets the tooltip show while the button is disabled. */}
        <Tooltip title={adminOnly ?? (channelId ? undefined : "Choose a channel first")}>
          <span style={{ display: "inline-block" }}>
            <Button size="small" icon={<SendOutlined />} loading={testing} disabled={!isAdmin || !channelId} onClick={sendTest}>
              Send test
            </Button>
          </span>
        </Tooltip>
      </Space>
      <div style={{ marginTop: 8, color: "var(--muted)", fontSize: 12 }}>
        Only Telegram channels can receive system alerts. Webhook channels are excluded because they trigger
        mitigation actions.
      </div>
    </Card>
  );
}
